import { UnionSmallIcon } from "@/assets/images/svgIcons/unionSmallIcon"
import { Button } from "@/components/ui/baseUi/button"
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/baseUi/command"
import { InputContainer } from "@/components/ui/forms/inputContainer"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { useStripeAccount, useStripeAvailableCountries } from "@/modules/finances/hooks/useStripeAccount"
import { cn } from "@/modules/lib/utils"
import { removeDiacritics } from "@/modules/utils/stringUtils"
import StripeLogo from "@images/payment/stripe-logo.svg"
import { word } from "@modules/core/utils/i18n"
import { StripeCountryCode } from "@modules/payment/stripeTypes"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ChevronsUpDownIcon, LoaderCircle } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"

export function BoxOnboardingStripeSecondPage({
  handlePreviousPageNavigation,
}: {
  handlePreviousPageNavigation: () => void
}) {
  const [selectedCountry, setSelectedCountry] = useState<StripeCountryCode | undefined>()
  const [openDropdown, setOpenDropdown] = useState(false)

  const popup = useConfirmPopup()
  const { createStripeAccount, loading } = useStripeAccount()
  const { availableStripeCountries } = useStripeAvailableCountries()

  const countryOptions = availableStripeCountries
    .map((stripePlatform) =>
      stripePlatform.countries.map((country) => ({
        value: country,
        label: word(`global.country.${country}`),
        stripePlatform: stripePlatform.stripePlatform,
      })),
    )
    .flat()
    .sort((a, b) => {
      return a.label.localeCompare(b.label)
    })

  const handleSave = (selectedCountry: StripeCountryCode) =>
    popup.show({
      title: word("box.stripe.onboarding.screen2.modal.title"),
      message: word("box.stripe.onboarding.screen2.modal.content"),
      accept: () => {
        const selectedStripePlatform = countryOptions.find(
          (country) => country.value === selectedCountry,
        )?.stripePlatform

        if (!selectedStripePlatform) {
          throw new Error("Selected stripe platform not found")
        }

        return createStripeAccount(selectedCountry, selectedStripePlatform).catch((error: unknown) => {
          console.error(error)
          if (error instanceof Error) {
            toast.error(error.message, {
              classNames: errorToastClassName(),
            })
          } else {
            toast.error(word("global.error.default"), {
              classNames: errorToastClassName(),
            })
          }
        })
      },
      classNames: {
        dialogFooterConfirm: "flex-1",
        dialogFooterCancel: "flex-1",
        dialogFooter: "flex flex-row justify-around w-full",
      },
      footerProps: {
        confirmText: word("global.continue"),
      },
    })

  return (
    <div className="relative h-screen w-full p-12">
      {/* HEARDER */}
      <div className="flex w-full flex-col items-center gap-2 text-center">
        <UnionSmallIcon color="currentColor" />

        <Text fontStyle="HL" className="mt-3">
          {word("box.stripe.onboarding.title")}
        </Text>
        {/* TEXT FOR DESKTOP & TABLET */}
        <Text fontStyle="BL" className="mt-1 hidden sm:flex">
          {word("box.stripe.onboarding.screen2.subtitle1")}
        </Text>
        <Text fontStyle="BL" className="hidden sm:flex">
          {word("box.stripe.onboarding.screen2.subtitle2")}
        </Text>
        {/* TEXT FOR SMALL DEVICE */}
        <Text fontStyle="BL" className="mt-1 flex sm:hidden">
          {word("box.stripe.onboarding.screen2.subtitle1")} {word("box.stripe.onboarding.screen2.subtitle2")}
        </Text>
      </div>

      {/* CONTENT */}
      <div className="mt-6 mb-6 flex flex-col items-center gap-3">
        <img src={StripeLogo} width={260} className="mt-1 mb-2" />
        <div style={{ width: "456px" }} className="flex flex-col">
          <Text fontStyle="BM" className="text-center">
            {word("box.stripe.onboarding.screen2.content")}
          </Text>

          <div className="mt-1 flex w-full flex-col gap-4 px-4">
            <InputContainer label={word("global.country.label").toUpperCase()} isRequiredInput>
              <Popover open={openDropdown} onOpenChange={setOpenDropdown}>
                <PopoverTrigger
                  render={
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openDropdown}
                      className="h-11 w-full justify-between"
                    >
                      {selectedCountry
                        ? countryOptions.find((countryOption) => countryOption.value === selectedCountry)?.label
                        : word("box.form.address.country.label")}

                      <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  }
                />
                <PopoverContent className="w-65 border-0 p-0" align="start">
                  <Command
                    className="**:data-[slot=command-input-wrapper]:bg-neutral-50"
                    filter={(value, search) => {
                      if (removeDiacritics(value.toLowerCase()).includes(removeDiacritics(search.toLowerCase())))
                        return 1
                      return 0
                    }}
                  >
                    <CommandInput placeholder={word("global.searchCountry")} className="text-textDefault-body" />
                    <CommandList>
                      <CommandEmpty>{word("global.searchCountryNotFound")}</CommandEmpty>
                      {countryOptions.map((countryOption) => (
                        <CommandItem
                          key={countryOption.value}
                          value={countryOption.label}
                          onSelect={(currentValue) => {
                            const foundCountry = countryOptions.find(
                              (countryOption) => countryOption.label === currentValue,
                            )
                            setSelectedCountry(foundCountry?.value)
                            setOpenDropdown(false)
                          }}
                          className="cursor-pointer hover:bg-neutral-200"
                        >
                          <CheckIcon
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedCountry === countryOption.value ? "opacity-100" : "opacity-0",
                            )}
                          />
                          {countryOption.label}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </InputContainer>
          </div>

          <Button variant="underline" size="lg" className="w-fit self-end">
            <Link to="mailto:contact@hustleup-app.com?subject=I%20can't%20find%20my%20country%20&body=">
              {word("program.stripe.onboarding.countryNotFound")}
            </Link>
          </Button>
        </div>
      </div>
      {/* BUTTON */}
      <div className="mb-4 flex flex-row items-center justify-center gap-2">
        <Button
          variant="outline"
          size="lg"
          onClick={() => {
            handlePreviousPageNavigation()
          }}
          className="w-fit"
        >
          <ArrowLeftIcon color="currentColor" />
          {word("global.previous")}
        </Button>

        <Button
          variant="primary"
          size="lg"
          onClick={() => selectedCountry && handleSave(selectedCountry)}
          disabled={!selectedCountry || loading}
          className="w-fit"
        >
          {word("box.stripe.onboarding.screen2.button")}
          {loading ? <LoaderCircle className="animate-spin" /> : <ArrowRightIcon color="currentColor" />}
        </Button>
      </div>
    </div>
  )
}
